import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import theme from "../../../theme";
import { PrimaryButton } from '../../../SystemDesign/styles';
import AuthModal from '../../Auth/AuthModal';
import { useAuth } from '../../../context/AuthContext';
import { scroller } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../../LanguageSelector/LanguageSelector';

const navItems = ['Accueil', 'Nos salons', 'Nos services'];

function NavBar({ appearance = {} }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeItem, setActiveItem] = React.useState('Home'); // Set default active item
  const [authModalOpen, setAuthModalOpen] = React.useState(false);
  const [userType, setUserType] = React.useState('client'); // 'client' or 'professional'

  const navigate = useNavigate();
  const { isAuthenticated, getUserType } = useAuth();
  const { t, i18n } = useTranslation();
  const foreground = appearance.foreground || theme.colors.grey[900];
  const activeColor = appearance.activeColor || theme.colors.primary[500];
  const underlineColor = appearance.underlineColor || theme.colors.primary[500];
  const borderColor = appearance.borderColor || theme.colors.grey[200];
  const fieldBorderColor = appearance.fieldBorderColor || theme.colors.grey[500];

  // Determine logo based on language
  const logoSrc = i18n.language === 'ar' ? '/assets/logo_ar.png' : '/assets/logo.png';

  // Check if user is authenticated and is a client
  const isClientAuthenticated = isAuthenticated() && getUserType() === 'client';

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleItemClick = (item) => {
    setActiveItem(item);

    // Handle navigation based on the clicked item
    if (item === 'Accueil') {
      // Scroll to hero section
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (item === 'Nos salons') {
      // Scroll to Nos Salons section
      scroller.scrollTo('nosSalonSection', {
        duration: 800,
        delay: 0,
        smooth: 'easeInOutQuart'
      });
    } else if (item === 'Nos services') {
      // Scroll to Nos Services (Pro) section
      scroller.scrollTo('proSection', {
        duration: 800,
        delay: 0,
        smooth: 'easeInOutQuart'
      });
    }
  };

  const handleOpenAuthModal = (type) => {
    setUserType(type);
    setAuthModalOpen(true);
  };

  const handleClientButtonClick = () => {
    if (isClientAuthenticated) {
      // Navigate to client dashboard
      navigate('/client');
    } else {
      // Open auth modal for client login
      handleOpenAuthModal('client');
    }
  };

  const handleCloseAuthModal = () => {
    setAuthModalOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        position="fixed" // Add this to fix the navbar to the top
        sx={{
          backgroundColor: appearance.backgroundColor || 'transparent',
          boxShadow: "none",
          borderBottom: `1px solid ${borderColor}`,
          backdropFilter: 'blur(10px)',
          color: foreground,
          textShadow: appearance.textShadow || 'none',
          zIndex: 1100,
          transition: 'background-color 0.45s ease, border-color 0.45s ease, color 0.45s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Left side - Logo/Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                display: { xs: 'inline-flex', lg: 'none' },
                color: foreground,
                transition: 'color 0.45s ease',
              }}
            >
              <MenuIcon />
            </IconButton>
            <img
              src={logoSrc}
              alt="Saloone"
              style={{
                height: '60px',
                width: 'auto',
                filter: appearance.logoFilter || 'none',
                transition: 'filter 0.45s ease',
              }}
            />
          </Box>

          {/* Center - Navigation Items */}
          <Box sx={{
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'center',
            flex: 1
          }}>
            {navItems.map((item) => (
              <Button
                key={item}
                onClick={() => handleItemClick(item)}
                sx={{
                  color: activeItem === item ? activeColor : foreground,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  mx: 2,
                  position: 'relative',
                  fontWeight: activeItem === item ? 'bold' : 'normal',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: activeItem === item ? '80%' : '0%',
                    height: '2px',
                    backgroundColor: underlineColor,
                    transition: 'width 0.3s ease'
                  },
                  '&:hover::after': {
                    width: '80%'
                  }
                }}
              >
                {item === 'Accueil' ? t('nav.home') : item === 'Nos salons' ? t('nav.salons') : t('nav.services')}
              </Button>
            ))}
          </Box>

          {/* Right side - Login Buttons */}
          <Box sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            gap: 1.5 // Space between buttons
          }}>
            <LanguageSelector sx={{
              minWidth: 110,
              color: foreground,
              transition: 'color 0.45s ease',
              '& .MuiSelect-select': {
                color: foreground,
                textShadow: appearance.textShadow || 'none',
              },
              '& .MuiSvgIcon-root': {
                color: foreground,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: fieldBorderColor,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: foreground,
              },
            }} />
            {/* Client Login Button - Outlined style */}
            <Button
              onClick={handleClientButtonClick}
              style={{
                ...PrimaryButton,
                backgroundColor: isClientAuthenticated ? theme.colors.primary[500] : theme.colors.secondary[500],
                fontSize: '14px',
                padding: '6px 16px',
                height: '34px',
                borderRadius: '20px',
                whiteSpace: 'nowrap',
                minWidth: '110px',
                textTransform: 'none',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isClientAuthenticated ? t('actions.my_space') : t('actions.book')}
            </Button>

            {/* Professional Login Button - Filled style */}
            <Button
              onClick={() => handleOpenAuthModal('professional')}
              style={{
                ...PrimaryButton,
                backgroundColor: theme.colors.primary[500],
                fontSize: '14px',
                // padding: '6px 16px',
                height: '34px',
                borderRadius: '20px',
                textTransform: 'none',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('actions.im_pro')}
            </Button>

            {/* Auth Modal */}
            <AuthModal
              open={authModalOpen}
              onClose={handleCloseAuthModal}
              userType={userType}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 250,
              backgroundColor: theme.colors.secondary[200],
              color: theme.colors.primary[900],
            },
          }}
        >
          <Box
            onClick={handleDrawerToggle}
            sx={{ textAlign: 'center', p: 2, color: theme.colors.primary[900] }}
          >
            <img
              src={logoSrc}
              alt="Saloone"
              style={{
                height: '40px',
                width: 'auto',
                marginBottom: '8px'
              }}
            />
            <Divider sx={{ borderColor: theme.colors.secondary[600], opacity: 0.6 }} />
            <List>
              {navItems.map((item) => (
                <ListItem key={item} disablePadding>
                  <ListItemButton
                    sx={{
                      textAlign: 'left',
                      color: theme.colors.primary[900],
                      '&:hover': {
                        backgroundColor: theme.colors.secondary[600],
                        color: theme.colors.text.inverse,
                      },
                    }}
                    onClick={() => handleItemClick(item)}
                  >
                    <ListItemText
                      primary={item === 'Accueil' ? t('nav.home') : item === 'Nos salons' ? t('nav.salons') : t('nav.services')}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1, borderColor: theme.colors.secondary[600], opacity: 0.6 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <LanguageSelector sx={{ width: '100%' }} />
              <Button
                onClick={handleClientButtonClick}
                style={{
                  ...PrimaryButton,
                  backgroundColor: isClientAuthenticated ? theme.colors.primary[500] : theme.colors.secondary[700],
                  fontSize: '14px',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.text.inverse
                }}
              >
                {isClientAuthenticated ? t('actions.my_space') : t('actions.book')}
              </Button>
              <Button
                onClick={() => handleOpenAuthModal('professional')}
                style={{
                  ...PrimaryButton,
                  backgroundColor: theme.colors.primary[500],
                  fontSize: '14px',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  whiteSpace: 'normal',
                  lineHeight: 1.3,
                  textAlign: 'center',
                  wordBreak: 'break-word'
                }}
              >
                {t('actions.im_pro')}
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>
    </Box>
  );
}

export default NavBar;
